<template>
    <div class="gv-product-form">
        <LoaderComponent v-if="loading > 0" />
        <div v-else>
            <section class="gv-section" aria-labelledby="gv-type-title">
                <h6 id="gv-type-title" class="gv-section-title">Type de produit</h6>
                <div class="gv-type-choice" role="radiogroup" aria-labelledby="gv-type-title">
                    <label class="gv-type-option" :class="{ 'is-selected': !product_type }">
                        <input type="radio" class="visually-hidden" name="gv-product-type" :value="false" v-model="product_type" />
                        <span class="gv-type-name">{{ offerLabel }}</span>
                        <span class="gv-type-help">Prix fixe : repas, soin, activité… Peut être une formule.</span>
                    </label>
                    <label class="gv-type-option" :class="{ 'is-selected': product_type }">
                        <input type="radio" class="visually-hidden" name="gv-product-type" :value="true" v-model="product_type" />
                        <span class="gv-type-name">Chèque cadeau</span>
                        <span class="gv-type-help">Montant prédéfini ou libre, à dépenser dans l'établissement.</span>
                    </label>
                </div>
            </section>

            <section class="gv-section" aria-labelledby="gv-info-title">
                <h6 id="gv-info-title" class="gv-section-title">Informations</h6>
                <div class="gv-field">
                    <label class="form-label" for="gv-name">{{ nameLabel }} <small>*</small></label>
                    <div>
                        <input id="gv-name" maxlength="70" type="text" class="form-control" v-model="product.name" />
                        <small class="text-muted">{{ (product.name || "").length }}/70 caractères</small>
                        <ShowErrors class="d-block" :errors="errors.form" errorKey="name" />
                    </div>
                </div>
                <gift-vouchers-category-component
                    :categories="categories"
                    :current-category="currentCategory"
                    :errors="errors.form"
                    v-model="customCategoryName"
                    @category-changed="currentCategory = $event" />
                <div class="gv-field">
                    <label class="form-label" :for="descriptionId">Description <small>*</small></label>
                    <div>
                        <TextAreaComponentOld
                            :maxNbChar="150"
                            :txtAreaId="descriptionId"
                            :txtAreaObj="product"
                            txtAreaObjKey="description"
                            txtAreaRows="3" />
                        <ShowErrors class="d-block" :errors="errors.form" errorKey="description" />
                    </div>
                </div>
                <div class="gv-field">
                    <span class="form-label">Image <small class="text-muted">(recommandée)</small></span>
                    <div>
                        <image-uploader
                            :public-image-uri="product.img !== null ? product.img.public_uri : null"
                            :image-path="product.img !== null ? product.img.path : null"
                            :upload-uri="imgPostUri"
                            :delete-uri="imgPostUri"
                            @image-uploaded="product.img = $event"
                            @image-deleted="product.img = null"
                            :maxSize="10" />
                    </div>
                </div>
            </section>

            <section v-if="!product_type" class="gv-section" aria-labelledby="gv-price-title">
                <h6 id="gv-price-title" class="gv-section-title">Prix et TVA</h6>
                <div class="gv-field">
                    <label class="form-label" for="gv-is-formula">Formule</label>
                    <div class="d-flex align-items-center gap-2 lead-switch">
                        <input id="gv-is-formula" type="checkbox" class="switch align-self-center is-rounded" v-model="is_formula" />
                        <label class="form-label mb-0" @click="is_formula = !is_formula"></label>
                        <span class="pointer" @click="is_formula = !is_formula"
                            >Composée de sous-produits<span v-if="!isTvaDisabled"> avec leur propre TVA</span></span
                        >
                    </div>
                </div>
                <div v-if="is_formula" class="mb-3">
                    <GiftVoucherFormulaComponents :components="product.components" :tva-disabled="isTvaDisabled" :errors="errors.form" />
                    <ShowErrors class="d-block" :errors="errors.form" errorKey="price" />
                </div>
                <template v-else>
                    <div class="gv-field">
                        <label class="form-label" for="gv-price">Prix TTC <small>*</small></label>
                        <div>
                            <div class="input-group gv-input-short">
                                <input
                                    id="gv-price"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    inputmode="decimal"
                                    class="form-control"
                                    v-model="product.price" />
                                <span class="input-group-text">€</span>
                            </div>
                            <ShowErrors class="d-block" :errors="errors.form" errorKey="price" />
                        </div>
                    </div>
                    <div class="gv-field" v-if="!isTvaDisabled">
                        <label class="form-label" for="gv-tva">TVA <small>*</small></label>
                        <div>
                            <select id="gv-tva" v-model="product.tva" class="form-select gv-input-short">
                                <option :value="null">--</option>
                                <option v-for="rate in taxRates" :key="rate" :value="rate">{{ formatRate(rate) }} %</option>
                            </select>
                            <ShowErrors class="d-block" :errors="errors.form" errorKey="tva" />
                        </div>
                    </div>
                </template>
                <div class="gv-field">
                    <label class="form-label" for="gv-nb-pers">Nombre de personnes <small>*</small></label>
                    <div>
                        <div class="input-group gv-input-short">
                            <input id="gv-nb-pers" type="number" min="1" step="1" class="form-control" v-model="product.nb_pers" />
                            <span class="input-group-text">pers.</span>
                        </div>
                        <ShowErrors class="d-block" :errors="errors.form" errorKey="nb_pers" />
                    </div>
                </div>
            </section>

            <section v-else class="gv-section" aria-labelledby="gv-amounts-title">
                <h6 id="gv-amounts-title" class="gv-section-title">Montants</h6>
                <div class="gv-field">
                    <label class="form-label" for="gv-amount-to-add">Montants prédéfinis</label>
                    <div>
                        <div class="d-flex flex-wrap gap-2">
                            <div class="input-group gv-input-short">
                                <input
                                    id="gv-amount-to-add"
                                    type="number"
                                    min="1"
                                    step="1"
                                    class="form-control"
                                    v-model="amount_to_add"
                                    aria-describedby="gv-amounts-help"
                                    @keydown.enter.prevent="addAmount()" />
                                <span class="input-group-text">€</span>
                            </div>
                            <button type="button" class="btn btn-sm btn-outline-success btn-circle" @click="addAmount()">
                                <feather type="plus" aria-hidden="true" /> Ajouter
                            </button>
                        </div>
                        <small id="gv-amounts-help" class="text-muted d-block">
                            Montants entiers proposés au client. Au moins un est requis si les montants libres ne sont pas autorisés.
                        </small>
                        <span class="text-danger d-block" v-if="errors.amountToAdd">{{ errors.amountToAdd }}</span>
                        <div class="mt-2 tags" v-if="product.predefined_amounts.length">
                            <span class="tag is-rounded" v-for="amount in product.predefined_amounts" :key="amount">
                                {{ amount }} €
                                <button type="button" class="delete" :aria-label="`Retirer ${amount} €`" @click="delAmount(amount)"></button>
                            </span>
                        </div>
                        <ShowErrors class="d-block" :errors="errors.form" errorKey="predefined_amounts" />
                    </div>
                </div>
                <div class="gv-field">
                    <label class="form-label" for="gv-custom-amounts">Montants libres</label>
                    <div>
                        <div class="d-flex align-items-center gap-2 lead-switch">
                            <input
                                id="gv-custom-amounts"
                                type="checkbox"
                                class="switch align-self-center is-rounded"
                                v-model="product.allow_custom_amounts" />
                            <label class="form-label mb-0" @click="product.allow_custom_amounts = !product.allow_custom_amounts"></label>
                            <span class="pointer" @click="product.allow_custom_amounts = !product.allow_custom_amounts">
                                Le client saisit le montant de son choix
                            </span>
                        </div>
                        <ShowErrors class="d-block" :errors="errors.form" errorKey="allow_custom_amounts" />
                    </div>
                </div>
                <template v-if="product.allow_custom_amounts">
                    <div class="gv-field">
                        <label class="form-label" for="gv-min-amount">Montant minimum <small>*</small></label>
                        <div>
                            <div class="input-group gv-input-short">
                                <input id="gv-min-amount" type="number" min="1" step="0.01" class="form-control" v-model="product.min_amount" />
                                <span class="input-group-text">€</span>
                            </div>
                            <ShowErrors class="d-block" :errors="errors.form" errorKey="min_amount" />
                        </div>
                    </div>
                    <div class="gv-field">
                        <label class="form-label" for="gv-max-amount">Montant maximum <small>*</small></label>
                        <div>
                            <div class="input-group gv-input-short">
                                <input id="gv-max-amount" type="number" min="1" step="0.01" class="form-control" v-model="product.max_amount" />
                                <span class="input-group-text">€</span>
                            </div>
                            <ShowErrors class="d-block" :errors="errors.form" errorKey="max_amount" />
                        </div>
                    </div>
                </template>
            </section>

            <section class="gv-section" aria-labelledby="gv-usage-title">
                <h6 id="gv-usage-title" class="gv-section-title">Utilisation</h6>
                <div class="gv-field">
                    <label class="form-label" for="gv-validity">Validité <small>*</small></label>
                    <div>
                        <div class="input-group gv-input-short">
                            <input id="gv-validity" type="number" min="1" step="1" class="form-control" v-model="product.validity" />
                            <span class="input-group-text">jours</span>
                        </div>
                        <ShowErrors class="d-block" :errors="errors.form" errorKey="validity" />
                    </div>
                </div>
                <div class="gv-field" v-if="hasBooking">
                    <span class="form-label">{{ $tl("labels.giftVouchers.products.service", restaurantId) }} <small>*</small></span>
                    <div>
                        <ShowErrors class="d-block mb-2" :errors="errors.form" errorKey="services" />
                        <div class="mb-2">
                            <label class="form-label container-box" style="margin-bottom: 0; width: 100%">
                                <input type="checkbox" v-model="all_services" />
                                <span class="checkmark"></span> Tous
                            </label>
                        </div>
                        <div class="mb-2" v-for="service in services" :key="service.id">
                            <label class="form-label container-box" style="margin-bottom: 0; width: 100%">
                                <input type="checkbox" v-model="service.checked" @change="serviceChecked(service)" />
                                <span class="checkmark"></span> {{ service.name }} ({{ service.category }})
                            </label>
                        </div>
                    </div>
                </div>
                <div class="gv-field" v-if="product_type && hasClickAndCollect && (collect_enabled || delivery_enabled || dispatch_enabled)">
                    <span class="form-label">Module Vente en ligne</span>
                    <div>
                        <div class="mb-2" v-if="collect_enabled">
                            <label class="form-label container-box" style="margin-bottom: 0; width: 100%">
                                <input type="checkbox" v-model="product.collect_enabled" />
                                <span class="checkmark"></span> {{ COLLECT_TYPE_COLLECT.label }}
                            </label>
                        </div>
                        <ShowErrors class="d-block mb-2" :errors="errors.form" errorKey="collect_enabled" />
                        <div class="mb-2" v-if="delivery_enabled">
                            <label class="form-label container-box" style="margin-bottom: 0; width: 100%">
                                <input type="checkbox" v-model="product.delivery_enabled" />
                                <span class="checkmark"></span> {{ COLLECT_TYPE_DELIVERY.label }}
                            </label>
                        </div>
                        <ShowErrors class="d-block mb-2" :errors="errors.form" errorKey="delivery_enabled" />
                        <div class="mb-2" v-if="dispatch_enabled">
                            <label class="form-label container-box" style="margin-bottom: 0; width: 100%">
                                <input type="checkbox" v-model="product.dispatch_enabled" />
                                <span class="checkmark"></span> {{ COLLECT_TYPE_DISPATCH.label }}
                            </label>
                        </div>
                        <ShowErrors class="d-block mb-2" :errors="errors.form" errorKey="dispatch_enabled" />
                    </div>
                </div>
                <div class="gv-field">
                    <label class="form-label" for="gv-terms">Conditions d'utilisation</label>
                    <div>
                        <textarea id="gv-terms" class="form-control" rows="3" v-model="product.terms_of_use"></textarea>
                        <ShowErrors class="d-block" :errors="errors.form" errorKey="terms_of_use" />
                    </div>
                </div>
            </section>

            <section v-if="!product_type" class="gv-section" aria-labelledby="gv-options-title" :aria-busy="optionsBusy > 0">
                <h6 id="gv-options-title" class="gv-section-title">Options</h6>
                <p class="gv-section-help">
                    Suppléments proposés à l'achat (par exemple une bouteille de vin). Cochez ceux disponibles pour cette offre ; les options sont
                    communes à tous les produits de l'établissement.
                </p>
                <ul class="gv-options" v-if="gv_products_options.length">
                    <li v-for="gv_products_option in gv_products_options" :key="gv_products_option.id" class="gv-option">
                        <label class="form-label container-box mb-0" style="width: auto">
                            <input type="checkbox" v-model="gv_products_option.active" />
                            <span class="checkmark"></span>
                            {{ gv_products_option.name }}
                        </label>
                        <span class="gv-option-price">{{
                            gv_products_option.include ? "Inclus" : "+ " + formatCurrency(gv_products_option.price)
                        }}</span>
                        <span class="gv-option-actions">
                            <button
                                type="button"
                                class="btn btn-sm btn-success btn-square"
                                :aria-label="`Modifier l'option ${gv_products_option.name}`"
                                v-tooltip="getTooltip('Modifier')"
                                @click="editProductOption(gv_products_option)">
                                <feather type="edit" aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                class="btn btn-sm btn-danger btn-square"
                                :aria-label="`Supprimer l'option ${gv_products_option.name}`"
                                v-tooltip="getTooltip('Supprimer')"
                                @click="deleteProductOption(gv_products_option)">
                                <feather type="trash-2" aria-hidden="true" />
                            </button>
                        </span>
                    </li>
                </ul>
                <p v-else class="text-muted small">Aucune option pour le moment.</p>
                <ShowErrors class="d-block" :errors="errors.form" errorKey="product_options" />
                <button type="button" @click="addProductOption()" class="btn btn-sm btn-outline-success btn-circle mt-2">
                    <feather type="plus" aria-hidden="true" /> Créer une option
                </button>
            </section>

            <add-product-option-modal
                v-if="showAddProductOptionModal"
                :product_option="product_option"
                @saved="productOptionSaved"
                @close="showAddProductOptionModal = false" />
        </div>
    </div>
</template>

<script>
import LoaderComponent from "../LoaderComponent.vue";
import ImageUploader from "../forms/ImageUploaderComponent.vue";
import TextAreaComponentOld from "../forms/TextAreaComponentOld";
import ShowErrors from "../errors/ShowErrors";
import addProductOptionModal from "../../components/Modals/giftVouchers/addProductOptionModal";
import CollectTypesEnum from "../../mixins/enums/click_and_collect/CollectTypesEnum";
import GiftVouchersCategoryComponent from "./addGiftVouchersProduct/GiftVouchersCategoryComponent.vue";
import GiftVoucherFormulaComponents from "./addGiftVouchersProduct/GiftVoucherFormulaComponents.vue";
import { FORMULA_TAX_RATES, componentsForApi, formulaTotalCents, startFormula, toCents } from "./addGiftVouchersProduct/formulaComposition.mjs";

function defaultProductOption() {
    return {
        id: null,
        include: 1,
        name: "",
        price: null,
        tva: null,
    };
}

export default {
    data() {
        return {
            loading: 0,
            errors: {
                form: null,
                amountToAdd: null,
            },
            product_type: this.product.type === "amount",
            is_formula: Array.isArray(this.product.components) && this.product.components.length > 0,
            taxRates: FORMULA_TAX_RATES,
            saving: false,
            optionsBusy: 0,
            amount_to_add: null,
            services: [],
            all_services: false,
            showAddProductOptionModal: false,
            product_option: null,
            gv_products_options: [],
            collect_enabled_: false,
            delivery_enabled_: false,
            dispatch_enabled_: false,
            categories: [],
            currentCategory: null,
            customCategoryName: null,
        };
    },
    mixins: [CollectTypesEnum],
    props: {
        product: {
            type: Object,
            required: true,
        },
    },
    computed: {
        offerLabel() {
            return this.isYservices ? "Prestation" : "Offre";
        },
        nameLabel() {
            if (this.product_type) {
                return "Nom du chèque cadeau";
            }
            return this.isYservices ? "Nom de la prestation" : "Nom de l'offre";
        },
        descriptionId() {
            return "gv-description";
        },
        isTvaDisabled() {
            return this.$store.getters["restaurants/isTvaDisabled"](this.restaurantId);
        },
        hasBooking() {
            return this.$store.getters["restaurants/hasModule"]("booking", this.restaurantId);
        },
        hasClickAndCollect() {
            return this.$store.getters["restaurants/hasModule"]("click-and-collect", this.restaurantId);
        },
        collect_enabled() {
            return this.hasClickAndCollect && this.collect_enabled_;
        },
        delivery_enabled() {
            return this.hasClickAndCollect && this.delivery_enabled_;
        },
        dispatch_enabled() {
            return this.hasClickAndCollect && this.dispatch_enabled_;
        },
        imgPostUri() {
            let uri = `/api/gift_vouchers/${this.restaurantId}/products/img`;

            if (this.product.id !== null) {
                uri += `/${this.product.id}`;
            }

            return uri;
        },
        restaurantId() {
            return Number.parseInt(this.$route.params.restaurant_id);
        },
        nbServicesChecked() {
            return this.services.reduce((count, service) => (service.checked ? count + 1 : count), 0);
        },
    },
    methods: {
        getFormattedData() {
            const data = {};

            Object.assign(data, this.product);

            (data.img = data.img !== null ? data.img.path : null), (data.services = this.product.services.join("-"));
            data.product_options = [];
            this.gv_products_options.forEach((gv_products_option) => {
                if (gv_products_option.active) {
                    data.product_options.push(gv_products_option.id);
                }
            });
            data.product_options = data.product_options.join("-");
            data.price = toCents(data.price);
            data.min_amount = toCents(data.min_amount);
            data.max_amount = toCents(data.max_amount);
            data.type = this.product_type ? "amount" : "menu";
            data.predefined_amounts = data.predefined_amounts.join("-");
            data.category = this.currentCategory;
            data.custom_category_name = this.customCategoryName;

            if (data.type === "menu" && this.is_formula) {
                data.components = componentsForApi(this.product.components, { tvaDisabled: this.isTvaDisabled });
                data.price = formulaTotalCents(this.product.components);
                data.tva = null;
            } else {
                data.components = [];
            }

            return data;
        },
        getCategories() {
            this.loading++;

            this.httpGet(`/api/gift_vouchers/${this.restaurantId}/gvCategories`)
                .then((response) => {
                    if (response !== false) {
                        this.categories = response.data.data;
                        if (this.currentCategory === null) {
                            this.currentCategory = this.categories[0].id;
                        }
                    }
                })
                .finally(() => this.loading--);
        },
        addProductOption() {
            this.product_option = defaultProductOption();
            this.showAddProductOptionModal = true;
        },
        editProductOption(product_option) {
            this.product_option = _.cloneDeep(product_option);
            this.showAddProductOptionModal = true;
        },
        productOptionSaved(id) {
            this.showAddProductOptionModal = false;
            // Keep the boxes ticked in this form: the option list is reloaded from product.product_options.
            this.product.product_options = this.gv_products_options.filter((option) => option.active).map((option) => option.id);
            if (id != null && !this.product.product_options.includes(id)) {
                this.product.product_options.push(id);
            }
            this.fetchProductOptions("optionsBusy");
        },
        deleteProductOption(product_option) {
            if (!confirm(`Supprimer l'option « ${product_option.name} » ? Elle sera retirée de tous les produits de l'établissement.`)) {
                return;
            }

            this.optionsBusy++;

            this.httpDelete(`/api/gift_vouchers/${this.restaurantId}/productsOptions/${product_option.id}`)
                .then((response) => {
                    if (response !== false) {
                        const index = this.gv_products_options.findIndex((po) => po.id === product_option.id);

                        if (index !== -1) {
                            this.gv_products_options.splice(index, 1);
                        }
                    }
                })
                .finally(() => this.optionsBusy--);
        },
        fetchServices() {
            this.loading++;

            this.services = [];

            this.httpGet(`/api/restaurants/${this.restaurantId}/services?without_slots=1&all=1`)
                .then((response) => {
                    if (response !== false) {
                        this.services = response.data.map((service) => ({
                            id: service.id,
                            name: service.name,
                            category: service.category,
                            checked: this.product.services.indexOf(service.id) !== -1,
                        }));

                        this.$nextTick(() => (this.all_services = this.nbServicesChecked === this.services.length));
                    }
                })
                .finally(() => this.loading--);
        },
        /**
         * The initial load hides the form; a reload after creating or editing an option keeps it visible.
         */
        fetchProductOptions(busyCounter = "loading") {
            this[busyCounter]++;

            this.httpGet(`/api/gift_vouchers/${this.restaurantId}/productsOptions`)
                .then((response) => {
                    if (response !== false) {
                        this.gv_products_options = response.data.gv_products_options.map((gv_products_option) => ({
                            ..._.cloneDeep(gv_products_option),
                            active: this.product.product_options.indexOf(gv_products_option.id) !== -1,
                        }));
                    }
                })
                .finally(() => this[busyCounter]--);
        },
        formatRate(rate) {
            return String(rate).replace(".", ",");
        },
        scrollToFirstError() {
            this.$nextTick(() => {
                const error = this.$el.querySelector(".text-danger");
                if (error) {
                    error.scrollIntoView({ block: "center", behavior: "smooth" });
                }
            });
        },
        save() {
            if (this.saving) {
                return;
            }
            this.saving = true;
            this.$emit("saving", true);

            this.errors.form = null;
            this.errors.amountToAdd = null;

            let promise = null;

            if (this.product.id === null) {
                promise = this.httpPost(`/api/gift_vouchers/${this.restaurantId}/products`, this.getFormattedData());
            } else {
                promise = this.httpPut(`/api/gift_vouchers/${this.restaurantId}/products/${this.product.id}`, this.getFormattedData());
            }

            promise
                .then((response) => {
                    if (response !== false) {
                        this.$emit("saved");
                    } else {
                        this.scrollToFirstError();
                    }
                })
                .finally(() => {
                    this.saving = false;
                    this.$emit("saving", false);
                });
        },
        addAmount() {
            const amount = Number(String(this.amount_to_add ?? "").replace(",", "."));

            this.errors.amountToAdd = null;

            if (!Number.isInteger(amount) || amount < 1) {
                this.errors.amountToAdd = "Merci de saisir un montant entier d'au moins 1 €";
                return;
            }

            if (this.product.predefined_amounts.indexOf(amount) === -1) {
                this.product.predefined_amounts.push(amount);
            } else {
                this.errors.amountToAdd = "Ce prix est déjà défini";
            }

            this.product.predefined_amounts.sort((a, b) => a - b);
            this.amount_to_add = null;
        },
        delAmount(amount) {
            this.errors.amountToAdd = null;

            const i = this.product.predefined_amounts.indexOf(amount);

            if (i !== -1) {
                this.product.predefined_amounts.splice(i, 1);
            }
        },
        serviceChecked(service) {
            if (service.checked && this.product.services.indexOf(service.id) === -1) {
                this.product.services.push(service.id);
            } else if (!service.checked) {
                this.all_services = false;

                const i = this.product.services.indexOf(service.id);

                if (i !== -1) {
                    this.product.services.splice(i, 1);
                }
            }
        },
        fetchClickAndCollectConfig() {
            this.loading++;

            this.httpGet(`/api/click_and_collect/${this.restaurantId}/settings`, { handleReject: false })
                .then((response) => {
                    if (response !== false) {
                        this.collect_enabled_ = response.data.is_click_and_collect_enabled;
                        this.delivery_enabled_ = response.data.is_delivery_enabled;
                        this.dispatch_enabled_ = response.data.is_dispatch_enabled;
                    }
                })
                .finally(() => this.loading--);
        },
    },
    watch: {
        is_formula(isFormula) {
            if (isFormula && this.product.components.length === 0) {
                this.product.components.push(...startFormula({ price: this.product.price, tva: this.product.tva }));
            }
        },
        "product.allow_custom_amounts": function (newVal) {
            if (!newVal) {
                this.product.min_amount = null;
                this.product.max_amount = null;
            }
        },
        all_services(newVal) {
            if (newVal) {
                this.services.forEach((service) => {
                    service.checked = true;
                    this.serviceChecked(service);
                });
            }
        },
    },
    components: {
        LoaderComponent,
        ImageUploader,
        TextAreaComponentOld,
        ShowErrors,
        addProductOptionModal,
        GiftVouchersCategoryComponent,
        GiftVoucherFormulaComponents,
    },
    created() {
        if (!Array.isArray(this.product.components)) {
            this.product.components = [];
        }

        if (this.hasBooking) {
            this.fetchServices();
        }

        if (this.hasClickAndCollect) {
            this.fetchClickAndCollectConfig();
        }

        this.fetchProductOptions();
        this.getCategories();

        this.currentCategory = !this.$_.isEmpty(this.product.gvCategory) ? this.product.gvCategory.id : null;
    },
};
</script>

<style scoped>
.gv-product-form {
    --gv-accent: var(--main-color-light, #30a4b7);
    --gv-accent-soft: #eaf6f8;
    --gv-line: #e3e8ec;
    --gv-muted: #6b7680;
}

@supports (color: color-mix(in srgb, red 50%, white)) {
    .gv-product-form {
        --gv-accent-soft: color-mix(in srgb, var(--gv-accent) 10%, white);
    }
}

.gv-section {
    margin-bottom: 1rem;
    padding: 1.1rem 1.25rem 1.25rem;
    border: 1px solid var(--gv-line);
    border-radius: 14px;
    background: #fff;
}

.gv-section-title {
    margin: 0 0 1rem;
    font-size: 0.95rem;
    font-weight: 600;
}

.gv-section-help {
    margin: -0.5rem 0 0.75rem;
    color: var(--gv-muted);
    font-size: 0.85rem;
}

.gv-field {
    display: grid;
    grid-template-columns: 13rem minmax(0, 1fr);
    gap: 0.25rem 1rem;
    align-items: start;
    margin-bottom: 0.85rem;
}

.gv-field:last-child {
    margin-bottom: 0;
}

.gv-field > .form-label {
    margin: 0;
    padding-top: 0.45rem;
}

.gv-input-short {
    max-width: 12rem;
}

.gv-product-form :deep(.input-group-text) {
    min-width: 2.75rem;
    justify-content: center;
    color: var(--gv-muted);
    font-size: 0.85rem;
}

.gv-type-choice {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

.gv-type-option {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    margin: 0;
    padding: 0.85rem 1rem;
    border: 1.5px solid var(--gv-line);
    border-radius: 12px;
    cursor: pointer;
    transition:
        border-color 0.15s ease,
        background-color 0.15s ease;
}

.gv-type-option:hover {
    border-color: var(--gv-accent);
}

.gv-type-option.is-selected {
    border-color: var(--gv-accent);
    background: var(--gv-accent-soft);
}

.gv-type-option:focus-within {
    outline: 2px solid #2f3b45;
    outline-offset: 2px;
}

.gv-type-name {
    font-weight: 600;
}

.gv-type-help {
    color: var(--gv-muted);
    font-size: 0.8rem;
}

.gv-options {
    margin: 0;
    padding: 0;
    list-style: none;
}

.gv-option {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    gap: 0.75rem;
    align-items: center;
    padding: 0.45rem 0;
    border-bottom: 1px solid var(--gv-line);
}

.gv-option:last-child {
    border-bottom: 0;
}

.gv-option-price {
    color: var(--gv-muted);
    font-variant-numeric: tabular-nums;
}

.gv-option-actions {
    display: inline-flex;
}

.gv-option-actions :deep(svg),
.gv-section .btn :deep(svg) {
    width: 16px;
    height: 16px;
}

@media (max-width: 767px) {
    .gv-section {
        padding: 1rem;
    }

    .gv-field {
        grid-template-columns: minmax(0, 1fr);
    }

    .gv-field > .form-label {
        padding-top: 0;
    }

    .gv-type-choice {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
